import React, { useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SlickRaw from "react-slick";
import { getAppConfig } from "@config/appConfig";
import {
    ADVERT_CATEGORY_SEASONAL_VOUCHER,
    SOURCE_BO_TRAILER,
    SOURCE_LOCAL_TRAILER,
    TYPE_MOVIE_DETAILS
} from "@config/config";
import { ApplicationContext } from "@context/ApplicationContext";
import PlayIcon from "@components/Icons/PlayIcon";
import TrailerButton from "@components/TrailerButton";
import LeftArrowIcon from "@components/V3/Icons/LeftArrowIcon";
import RightArrowIcon from "@components/V3/Icons/RightArrowIcon";
import "./style.scss";

const Slick = SlickRaw.default ?? SlickRaw;

const V3CarouselSkeleton = () => (
    <div className="v3-carousel v3-carousel--skeleton">
        <div className="v3-carousel__wrapper">
            <div className="v3-carousel__text-container">
                <div className="v3-carousel__text-content">
                    <div className="v3-carousel__skeleton-title" />
                    <div className="v3-carousel__skeleton-subtitle" />
                    <div className="v3-carousel__skeleton-subtitle v3-carousel__skeleton-subtitle--short" />
                    <div className="v3-carousel__skeleton-buttons">
                        <div className="v3-carousel__skeleton-button" />
                        <div className="v3-carousel__skeleton-button" />
                    </div>
                </div>
            </div>
            <div className="v3-carousel__skeleton-image" />
        </div>
    </div>
);

const PrevArrow = ({ onClick, currentSlide }) =>
    currentSlide === 0 ? null : (
        <button
            type="button"
            className="v3-carousel__arrow v3-carousel__arrow--left"
            onClick={onClick}
        >
            <LeftArrowIcon />
        </button>
    );

const NextArrow = ({ onClick }) =>
    (
        <button
            type="button"
            className="v3-carousel__arrow v3-carousel__arrow--right"
            onClick={onClick}
        >
            <RightArrowIcon />
        </button>
    );

const V3Carousel = () => {

    const { carouselImages, isLoadingCarousel } = useContext(ApplicationContext);
    const { cdnUrl } = getAppConfig();
    const [activeSlide, setActiveSlide] = useState(0);
    const [videoErrors, setVideoErrors] = useState({});
    const sliderRef = useRef(null);
    const fallbackImage = `${cdnUrl}asset/img/default-movie-poster-large.webp`;

    const settings = {
        className: carouselImages?.length > 1 ? "multiple-banners" : "",
        dots: true,
        arrows: true,
        autoplay: true,
        infinite: true,
        speed: 400,
        autoplaySpeed: 7000,
        cssEase: "ease-in-out",
        slidesToShow: 1.5,
        slidesToScroll: 1,
        initialSlide: 0,
        afterChange: (current) => setActiveSlide(current),
        beforeChange: (current, next) => {
            const totalSlides = carouselImages?.length || 0;
            if (current === totalSlides && next === 0) {
                sliderRef.current?.slickGoTo(0);
            }
        },
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        appendDots: (dots) => (
            <div className="slick-dots">
                {dots.map((_, index) => (
                    <div
                        role="button"
                        key={index}
                        className={`slick-dot ${index === activeSlide ? "active" : ""}`}
                        onClick={() => sliderRef.current?.slickGoTo(index)}
                    />
                ))}
            </div>
        ),
        responsive: [
            {
                breakpoint: 1023,
                settings: { slidesToShow: 1, slidesToScroll: 1 },
            },
        ],
    };

    if (isLoadingCarousel) return <V3CarouselSkeleton />;

    return (
        <div className="v3-carousel">
            <Slick ref={sliderRef} {...settings}>
                {carouselImages?.map((film, i) => {
                    const filmKey = film.bo_movie_id || `${film.banner_image_url}-${i}`;
                    const hasVideo =
                        !videoErrors[filmKey] &&
                        [SOURCE_BO_TRAILER, SOURCE_LOCAL_TRAILER].includes(
                            Number(film.image_source)
                        );

                    return (
                        <div className="v3-carousel__wrapper" key={filmKey}>
                            <div className="v3-carousel__text-container">
                                <div className="v3-carousel__text-content">
                                    {film.bo_movie_title && (
                                    <a
                                        className="v3-carousel__link v3-carousel__title"
                                        href={film.feature_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        >
                                        <h2 className="v3-carousel__title">{film.bo_movie_title}</h2>
                                        </a>
                                        )}
                                    <p className="v3-carousel__subtitle">{film.description}</p>
                                    <div className="v3-carousel__buttons">
                                        {Number(film.ad_category_id) === TYPE_MOVIE_DETAILS && (
                                            <Link
                                                className="button button__primary v3-carousel__button"
                                                to={{ pathname: film.feature_url }}
                                            >
                                                Book Now
                                            </Link>
                                        )}
                                        {Number(film?.ad_category_type) === ADVERT_CATEGORY_SEASONAL_VOUCHER && (
                                            <Link
                                                className="button button__primary v3-carousel__button"
                                                to={{ pathname: film.feature_url }}
                                            >
                                                Buy Now
                                            </Link>
                                        )}
                                        {film.trailer_url && (
                                            <TrailerButton
                                                extraClass="v3-carousel__trailer-button"
                                                type="youtube"
                                                key={`${film.trailer_url}-${i}`}
                                                trailerLink={film.trailer_url}
                                            >
                                                <PlayIcon className="v3-carousel__trailer-svg" />
                                                <span className="v3-carousel__trailer-text">Official Trailer</span>
                                            </TrailerButton>
                                        )}
                                    </div>
                                </div>
                                {film.bo_movie_rating &&
                                Number(film.ad_category_id) === TYPE_MOVIE_DETAILS && (
                                <div className="v3-carousel__classification">
                                    {film.bo_movie_rating}
                                </div>
                                )}
                            </div>
                            <a
                                className="v3-carousel__link"
                                href={film.feature_url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {hasVideo ? (
                                    <video
                                        className={`v3-carousel__trailer v3-carousel__trailer--${
                                    Number(film.image_source) === SOURCE_BO_TRAILER ? "local" : "bo"
                                }`}
                                        muted
                                        loop
                                        autoPlay
                                        playsInline
                                        onError={() =>
                                    setVideoErrors((prev) => ({ ...prev, [filmKey]: true }))
                                }
                            >
                                        <source src={film.banner_trailer_url} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                            ) : (
                                <img
                                    className="v3-carousel__image"
                                    src={film.banner_image_url || fallbackImage}
                                    alt={film.description}
                            />
                            )}
                            </a>
                        </div>
                );
                })}
            </Slick>
        </div>
    );
};

export default V3Carousel;
